import { useMemo } from 'react';
import { Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { dateUtils } from '../utils/dateUtils';
import { chartUtils } from '../utils/chartUtils';
import { formatNumber } from '../utils/dataUtils';
import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';

interface SessionTrendsTableProps {
  data: SessionTrendDataDto[];
  onItemClick?: (item: SessionTrendDataDto) => void;
}

export function SessionTrendsTable({ data, onItemClick }: SessionTrendsTableProps) {
  const sortedData = useMemo(() => dateUtils.sortChronologically(data), [data]);

  if (!sortedData?.length) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <p className="text-sm">No trend data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-2 py-3 text-left font-medium text-gray-700">Month</th>
            <th className="px-2 py-3 text-right font-medium text-gray-700">Sessions</th>
            <th className="px-2 py-3 text-right font-medium text-gray-700">Minutes</th>
            <th className="px-2 py-3 text-right font-medium text-gray-700">Avg Duration</th>
            <th className="px-2 py-3 text-right font-medium text-gray-700">Change</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((trend, index) => {
            const changeColor =
              trend.percentageChange === null
                ? 'text-gray-500'
                : trend.percentageChange > 0
                  ? 'text-green-600'
                  : 'text-red-600';
            const ChangeIcon =
              trend.percentageChange === null
                ? null
                : trend.percentageChange > 0
                  ? TrendingUp
                  : TrendingDown;

            return (
              <tr
                key={trend.monthStart.toString()}
                className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50"
                onClick={() => onItemClick?.(trend)}
              >
                <td className="px-2 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">
                      {index + 1}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {dateUtils.formatMonth(trend.monthStart)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-3 text-right font-medium text-gray-900">
                  {chartUtils.formatNumber(trend.sessionCount)}
                </td>
                <td className="px-2 py-3 text-right text-gray-600">
                  {formatNumber(Math.round(trend.totalMinutes))}
                </td>
                <td className="px-2 py-3 text-right text-gray-600">
                  {trend.sessionCount > 0
                    ? formatNumber(Math.round(trend.totalMinutes / trend.sessionCount))
                    : '0'}
                </td>
                <td className="px-2 py-3 text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <span className={`font-medium ${changeColor}`}>
                      {trend.percentageChange !== null
                        ? `${trend.percentageChange.toFixed(1)}%`
                        : '—'}
                    </span>
                    {ChangeIcon && <ChangeIcon className="h-3 w-3" />}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
